import { useEffect } from 'react';
import layerConfigStore from './layerConfig';
import layerStore from './layers';

// ---------------------------------------------------------------------------
// Tab layer locks — the shared mechanism for tabs that force map layers on while
// active (Land Rights / Zoning force parcels, Intelligence forces its per-section
// ids). Locking adds the ids to the shared render set and marks them in
// `layerStore.lockedLeafIds`; the picker renders a locked leaf checked + disabled,
// and every picker edit or wholesale clear skips it. Unlocking returns each id's
// render visibility to picker-controlled state. The persisted layer-config
// document is never touched by a lock, so a forced layer cannot leak into the
// user's saved visibility.
// ---------------------------------------------------------------------------

/**
 * Render visibility captured at lock time, keyed by leaf id. The restore fallback for the
 * legacy path (no layer-config document loaded), where render state is the only record of
 * what the picker had.
 */
const preLockVisibility = new Map<string, boolean>();

export interface TabLayerLockOptions {
  /**
   * When true (default) the lock pauses the visibility persistence subscriptions while held,
   * so the forced set is never written to saved layer prefs. The pause only applies while no
   * layer-config document is loaded: with a loaded document the persist source is the
   * doc-store's own sets, which locks never touch, and pausing would defer a mid-tab
   * hydrate's render mirror until the tab exits. Unlocking unpauses unconditionally, before
   * restoring, so the batched subscriptions fire on the restored set.
   */
  managePersistence?: boolean;
}

export interface TabLayerUnlockOptions extends TabLayerLockOptions {
  /**
   * When true (default) each unlocked id's render visibility is set back to the
   * picker-controlled state. Pass false when the caller immediately rewrites the render set
   * itself (Intelligence section switches and its full-snapshot restore on leave).
   */
  restore?: boolean;
}

/**
 * Force `layerIds` visible on the map and mark them tab-locked. Re-entrant: an already
 * locked id keeps its original pre-lock visibility (a StrictMode double-mount must not
 * snapshot the forced-on state as "prior").
 */
export function lockTabLayers(layerIds: readonly string[], options?: TabLayerLockOptions) {
  const managePersistence = options?.managePersistence ?? true;
  // Pause only when the shared render set is the persist source (legacy / loading / failed):
  // the document PUT observes the doc-store sets, which locks never touch.
  if (managePersistence && layerConfigStore.loadState !== 'loaded') layerStore.pausePersistence = true;
  for (const id of layerIds) {
    if (!layerStore.lockedLeafIds.has(id)) {
      preLockVisibility.set(id, layerStore.visibleLayerIds.has(id));
      layerStore.lockedLeafIds.add(id);
    }
    layerStore.visibleLayerIds.add(id);
  }
}

/**
 * Release tab locks. Unpauses persistence BEFORE the restore mutations so the batched
 * persistence subscriptions fire on the restored set, never the forced one. Ids that are
 * not currently locked are ignored.
 */
export function unlockTabLayers(layerIds: readonly string[], options?: TabLayerUnlockOptions) {
  const managePersistence = options?.managePersistence ?? true;
  const restore = options?.restore ?? true;
  // Unconditional (no loadState re-check): a document that hydrated mid-tab must not leave
  // a pause set at lock time stuck on. A no-change write never fires the pause watcher.
  if (managePersistence) layerStore.pausePersistence = false;
  for (const id of layerIds) {
    if (!layerStore.lockedLeafIds.has(id)) continue;
    layerStore.lockedLeafIds.delete(id);
    const fallbackVisible = preLockVisibility.get(id) ?? false;
    preLockVisibility.delete(id);
    if (restore) restoreLayerToPickerState(id, fallbackVisible);
  }
}

/**
 * Set a layer's render visibility back to what the picker controls: the persisted
 * layer-config document when one is loaded, otherwise `fallbackVisible` (captured before
 * the caller started overriding the layer). The `failed`-load fallback matches the picker's
 * own read source, which also falls back to the shared render set in that state.
 */
export function restoreLayerToPickerState(layerId: string, fallbackVisible: boolean) {
  const visible =
    layerConfigStore.loadState === 'loaded' ? layerConfigStore.visibleLeafIds.has(layerId) : fallbackVisible;
  if (visible) layerStore.visibleLayerIds.add(layerId);
  else layerStore.visibleLayerIds.delete(layerId);
}

/**
 * Force `layerIds` visible and locked for the lifetime of the calling tab component, and
 * return them to picker-controlled state on unmount. `layerIds` must be referentially
 * stable (a module-level constant) — a new array identity re-runs the lock cycle.
 */
export function useTabControlledLayers(layerIds: readonly string[]) {
  useEffect(() => {
    lockTabLayers(layerIds);
    return () => unlockTabLayers(layerIds);
  }, [layerIds]);
}

/**
 * Pre-tab visibility captured at a tab toggle's FIRST write for a leaf, keyed by leaf id and
 * consumed by `revertTabSyncedLayer`. Captured lazily at the write, never at mount: a tab
 * that mounts before the layer-config document hydrates must not bake the empty pre-hydrate
 * render set into a baseline, and a leaf the tab never toggled has no baseline to revert.
 */
const tabSyncBaselines = new Map<string, boolean>();

/**
 * Write path for a tab's own layer toggle (Environmental / GeoTech "Show on Map" /
 * "View in map"). When the layer-config document is loaded and the catalog can place the
 * leaf, the edit routes through the picker facade so the picker checkbox and the tab
 * toggle are one state, live in both directions. Otherwise it writes the render set
 * directly: the legacy path (facade falls through to the same mutation) and render-only
 * overlay leaves the catalog does not offer, where a facade edit would write an orphan id
 * into the persisted document without rendering anything. The catalog check is live, so a
 * leaf the DB starts offering becomes picker-synced with no code change.
 *
 * The first write for a leaf captures its current picker-visible state as the revert
 * baseline (see `revertTabSyncedLayer`).
 */
export function setTabSyncedLayerVisible(layerId: string, visible: boolean) {
  if (!tabSyncBaselines.has(layerId)) {
    tabSyncBaselines.set(
      layerId,
      layerConfigStore.loadState === 'loaded'
        ? layerConfigStore.visibleLeafIds.has(layerId)
        : layerStore.visibleLayerIds.has(layerId),
    );
  }
  writeTabSyncedLayerVisible(layerId, visible);
}

/** The routing body of `setTabSyncedLayerVisible`, shared with the revert (which must not re-capture). */
function writeTabSyncedLayerVisible(layerId: string, visible: boolean) {
  if (layerConfigStore.loadState === 'loaded' && !layerConfigStore.hasCatalogLeaf(layerId)) {
    if (visible) layerStore.visibleLayerIds.add(layerId);
    else layerStore.visibleLayerIds.delete(layerId);
    return;
  }
  layerConfigStore.setLayerVisible(layerId, visible);
}

/**
 * Revert a tab-toggled leaf to the baseline captured at the tab's first write, consuming
 * the baseline. No-op when the tab never wrote the leaf, so an untouched tab exit writes
 * nothing (in particular, it can never push a stale pre-hydrate baseline into the
 * persisted document).
 */
export function revertTabSyncedLayer(layerId: string) {
  const baseline = tabSyncBaselines.get(layerId);
  if (baseline === undefined) return;
  tabSyncBaselines.delete(layerId);
  writeTabSyncedLayerVisible(layerId, baseline);
}

/**
 * Revert a tab-toggled leaf on unmount, for tabs whose in-tab controls toggle a picker
 * layer without locking it (Environmental / GeoTech). The revert goes through the synced
 * write path, so a picker-synced leaf returns to its pre-tab state in BOTH the persisted
 * document and the render set; a leaf the tab never toggled, and every other leaf, is
 * untouched.
 */
export function useRestoreLayerOnUnmount(layerId: string) {
  useEffect(() => {
    return () => revertTabSyncedLayer(layerId);
  }, [layerId]);
}
